import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth } from '../index'; // Ensure auth is imported to get token

const SystemHealthContext = createContext();

export const useSystemHealth = () => useContext(SystemHealthContext);

export const SystemHealthProvider = ({ children }) => {
    const [monitors, setMonitors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [lastUpdated, setLastUpdated] = useState(null);
    const [error, setError] = useState(null);
    const [verdict, setVerdict] = useState('loading');
    const [stats, setStats] = useState({ healthy: 0, warning: 0, critical: 0, total: 0 });

    // Use Config from environment or fallback
    const API_BASE = process.env.REACT_APP_ADMIN_API_URL || "https://resido-backend-session-744649253655.asia-south1.run.app";

    const checkSystemHealth = async () => {
        setLoading(true);
        setError(null);
        try {
            const user = auth.currentUser;
            if (!user) {
                // Not logged in yet, skip fetch
                setLoading(false);
                return;
            }

            const token = await user.getIdToken();
            const response = await fetch(`${API_BASE}/admin/system-health`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch health status: ${response.statusText}`);
            }

            const data = await response.json();

            if (data.error) {
                setError(data.error);
                setVerdict('error');
            } else {
                const monitorList = data.monitors || [];
                setMonitors(monitorList);

                // Calculate stats
                const healthy = monitorList.filter(m => m.status === 'Healthy' || m.status === 'Clean').length;
                const warning = monitorList.filter(m => m.status === 'Paused' || m.status === 'Warning').length;
                const critical = monitorList.filter(m => m.status === 'Down' || m.status === 'Failed').length;

                setStats({
                    healthy,
                    warning,
                    critical,
                    total: monitorList.length
                });

                if (critical > 0) setVerdict('critical');
                else if (warning > 0) setVerdict('warning');
                else if (monitorList.length === 0) setVerdict('unconfigured');
                else setVerdict('healthy');
            }
            setLastUpdated(new Date());
        } catch (err) {
            console.error('Health Check Error:', err);
            setError(err.message);
            setVerdict('error');
        } finally {
            setLoading(false);
        }
    };

    // Initial fetch when auth state changes to signed in
    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
            if (user) {
                checkSystemHealth();
            } else {
                setMonitors([]);
                setVerdict('loading');
            }
        });
        return unsubscribe;
    }, []);

    const value = {
        monitors,
        loading,
        lastUpdated,
        error,
        verdict,
        stats,
        refreshHealth: checkSystemHealth
    };

    return (
        <SystemHealthContext.Provider value={value}>
            {children}
        </SystemHealthContext.Provider>
    );
};
